import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import {
    Type,
    Image as ImageIcon,
    Bold,
    Italic,
    Trash2,
    Square,
    Circle as CircleIcon,
    Plus,
    Minus,
    Underline,
    Triangle,
    Hexagon,
    Star,
    Download,
    ArrowUp,
    ArrowDown,
    Layers,
    Wand,
    Search
} from 'lucide-react';
import "../../styles/EditorCanvas.css";
import { FONTS, FONTS_WITHOUT_BOLD } from "../../config";
import { loadFonts } from "./utils/loadFonts";
import { patchTextObject } from "./utils/patchTextObject";

const EditorCanvas = ({ composedPoster }) => {
    const [templateId, setTemplateId] = useState(composedPoster?.poster_config?.template_id || "");
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const [canvas, setCanvas] = useState(null);
    const fabricRef = useRef(null);
    const [selectedObject, setSelectedObject] = useState(null);
    const [showElementMenu, setShowElementMenu] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const [showLayerMenu, setShowLayerMenu] = useState(false);
    const [fontSize, setFontSize] = useState(20);
    const [currentColor, setCurrentColor] = useState("#ffffff");
    const [canvasBackgroundColor, setCanvasBackgroundColor] = useState("#ffffff");
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const contextMenuRef = useRef(null);
    const shapesMenuRef = useRef(null);
    const downloadMenuRef = useRef(null);
    const layerMenuRef = useRef(null);
    const [fontsLoaded, setFontsLoaded] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [canvasObjects, setCanvasObjects] = useState(null);
    const isInitialized = useRef(false);

    const [showImageSelector, setShowImageSelector] = useState(false);
    const [showImageGenerator, setShowImageGenerator] = useState(false);
    const [imageOptions, setImageOptions] = useState([]);
    const [imagePrompt, setImagePrompt] = useState("");
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [selectedImageForGeneration, setSelectedImageForGeneration] = useState(null);
    const [selectedGenAIModel, setSelectedGenAIModel] = useState("flux_schnell");
    const [showSearchImages, setShowSearchImages] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [pexelsImages, setPexelsImages] = useState([]);
    const [isSearchingImages, setIsSearchingImages] = useState(false);
    const [posterDescription, setPosterDescription] = useState("");
    const [posterGenAIModel, setPosterGenAIModel] = useState("flux_schnell");
    const [templates, setTemplates] = useState([]);
    const [isGeneratingPoster, setIsGeneratingPoster] = useState(false);

    const isAnyPopupOpen = showImageSelector || showImageGenerator || showSearchImages;

    const currentFontSupportsBold = () => {
        if (!selectedObject || !selectedObject.fontFamily) return true;
        return !FONTS_WITHOUT_BOLD.includes(selectedObject.fontFamily);
    };

    const logCanvasObjects = () => {
        if (!fabricRef.current) return;

        const objects = fabricRef.current.getObjects();
        console.log("Objects on the canvas:", objects.length);
        objects.forEach((obj, index) => {
            console.log(`Object ${index + 1}:`, {
                type: obj.type,
                left: obj.left,
                top: obj.top,
                width: obj.width * obj.scaleX,
                height: obj.height * obj.scaleY,
                angle: obj.angle,
                fill: obj.fill,
                fontSize: obj.fontSize,
                fontFamily: obj.fontFamily,
                fontWeight: obj.fontWeight,
                fontStyle: obj.fontStyle,
                text: obj.text,
                src: obj.getSrc?.(),
                zIndex: obj.zIndex,
                customResolution: obj.customResolution,
            });
        });
    };

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await fetch('/api/v2/posters/templates');
                if (!response.ok) {
                    throw new Error('Failed to fetch templates');
                }
                const data = await response.json();
                setTemplates(data);
                if (data.length > 0 && !templateId) {
                    setTemplateId(data[0].id);
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };
        fetchTemplates();
    }, []);

    useEffect(() => {
        console.log("Starting font loading...");
        loadFonts()
            .then(() => {
                console.log("Fonts loaded successfully");
                setFontsLoaded(true);
            })
            .catch(err => {
                console.error("Error loading fonts:", err);
                setFontsLoaded(true);
            });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
                setContextMenu({ visible: false, x: 0, y: 0 });
            }
            if (shapesMenuRef.current && !shapesMenuRef.current.contains(event.target)) {
                setShowElementMenu(false);
            }
            if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
                setShowDownloadMenu(false);
            }
            if (layerMenuRef.current && !layerMenuRef.current.contains(event.target)) {
                setShowLayerMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!fontsLoaded) return;

        if (!fabricRef.current) {
            let canvasWidth = 800;
            let canvasHeight = 1130; // Explicitly set to match image size

            const objectsArray = Array.isArray(composedPoster.poster_objects)
                ? composedPoster.poster_objects
                : composedPoster;
            const config = composedPoster.poster_config;

            if (config) {
                canvasWidth = config.width !== undefined ? config.width : canvasWidth;
                canvasHeight = config.height !== undefined ? config.height : canvasHeight;
            }

            fabricRef.current = new fabric.Canvas("canvas", {
                width: canvasWidth,
                height: canvasHeight,
                backgroundColor: canvasBackgroundColor,
                selection: true,
                preserveObjectStacking: true,
            });
            setCanvas(fabricRef.current);
        }

        if (isInitialized.current && canvasObjects) {
            // Restore canvas from saved canvasObjects
            fabricRef.current.loadFromJSON(
                canvasObjects,
                () => {
                    fabricRef.current.getObjects().forEach(obj => {
                        if (obj.type === 'image') {
                            obj.set({
                                selectable: !obj.isBackground,
                                evented: !obj.isBackground,
                                lockMovementX: obj.isBackground,
                                lockMovementY: obj.isBackground,
                                lockScalingX: obj.isBackground,
                                lockScalingY: obj.isBackground,
                                lockRotation: obj.isBackground,
                            });
                        } else {
                            obj.set({
                                selectable: true,
                                evented: true,
                            });
                            if (obj.type === 'textbox') {
                                patchTextObject(obj);
                            }
                        }
                    });
                    fabricRef.current.renderAll();
                    logCanvasObjects();
                },
                { crossOrigin: "anonymous" }
            );
            return;
        }

        // Initial canvas setup
        if (composedPoster?.poster_config?.template_id && composedPoster.poster_config.template_id !== templateId) {
            setTemplateId(composedPoster.poster_config.template_id);
        }

        const initialBgColor = composedPoster?.background_color || "#ffffff";
        setCanvasBackgroundColor(initialBgColor);
        setCurrentColor(initialBgColor);
        fabricRef.current.setBackgroundColor(initialBgColor, fabricRef.current.renderAll.bind(fabricRef.current));

        const loadObjects = async () => {
            fabricRef.current.clear();

            const objectsArray = Array.isArray(composedPoster.poster_objects)
                ? composedPoster.poster_objects
                : composedPoster;
            const sortedObjects = objectsArray
                .filter(obj => obj.type !== "config")
                .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

            for (let i = 0; i < sortedObjects.length; i++) {
                const obj = sortedObjects[i];

                if (obj.type === "image") {
                    const imageUrl = obj.src;

                    const isBackground =
                        obj.left === 0 &&
                        obj.top === 0 &&
                        obj.width === fabricRef.current.width &&
                        obj.height === fabricRef.current.height;

                    await new Promise((resolve) => {
                        fabric.Image.fromURL(
                            imageUrl,
                            (img) => {
                                if (!img) {
                                    console.error(`Failed to load image: ${imageUrl}`);
                                    resolve();
                                    return;
                                }

                                img.set({
                                    left: obj.left || 0,
                                    top: obj.top || 0,
                                    width: obj.width || fabricRef.current.width,
                                    height: obj.height || fabricRef.current.height,
                                    originX: "left",
                                    originY: "top",
                                    selectable: !isBackground,
                                    evented: !isBackground,
                                    lockMovementX: isBackground,
                                    lockMovementY: isBackground,
                                    lockScalingX: isBackground,
                                    lockScalingY: isBackground,
                                    lockRotation: isBackground,
                                    isBackground: isBackground,
                                    zIndex: obj.zIndex || i,
                                    customWidth: obj.width,
                                    customHeight: obj.height,
                                    customResolution: obj.customResolution || null,
                                });

                                if (obj.clipPath) {
                                    const isCircular = obj.orientation === "circle";
                                    const clipPath = isCircular
                                        ? new fabric.Circle({
                                            left: obj.left,
                                            top: obj.top,
                                            radius: Math.min(obj.width, obj.height) / 2,
                                            originX: "left",
                                            originY: "top",
                                            absolutePositioned: true,
                                        })
                                        : new fabric.Rect({
                                            left: obj.left,
                                            top: obj.top,
                                            width: obj.width,
                                            height: obj.height,
                                            originX: "left",
                                            originY: "top",
                                            absolutePositioned: true,
                                        });
                                    img.clipPath = clipPath;
                                    console.log("ClipPath applied:", {
                                        isCircular,
                                        left: obj.left,
                                        top: obj.top,
                                    });
                                }

                                fabricRef.current.add(img);
                                fabricRef.current.moveTo(img, obj.zIndex || i);
                                console.log(`Image added (isBackground: ${isBackground}):`, {
                                    zIndex: img.zIndex,
                                    left: img.left,
                                    top: img.top,
                                    customWidth: img.customWidth,
                                    customHeight: img.customHeight,
                                    customResolution: img.customResolution,
                                });
                                resolve();
                            },
                            { crossOrigin: "anonymous" }
                        );
                    });
                } else if (obj.type === "text") {
                    let fontWeight = obj.fontWeight || "normal";
                    if (fontWeight === "bold" && FONTS_WITHOUT_BOLD.includes(obj.fontFamily)) {
                        fontWeight = "normal";
                        console.warn(`Bold not applied to ${obj.fontFamily}`);
                    }

                    const text = new fabric.Textbox(obj.text, {
                        left: obj.left,
                        top: obj.top,
                        width: obj.width,
                        fontSize: obj.fontSize,
                        fontFamily: obj.fontFamily,
                        fontWeight: fontWeight,
                        fontStyle: obj.fontStyle || "normal",
                        textAlign: obj.textAlign || "left",
                        fill: obj.fill || "#000000",
                        underline: obj.underline || false,
                        selectable: true,
                        evented: true,
                        zIndex: obj.zIndex || i,
                    });
                    patchTextObject(text);
                    fabricRef.current.add(text);
                    fabricRef.current.moveTo(text, obj.zIndex || i);
                    console.log("Text added:", {
                        zIndex: text.zIndex,
                        left: text.left,
                        top: text.top,
                    });
                }
            }

            fabricRef.current.renderAll();
            setCanvasObjects(fabricRef.current.toJSON(["zIndex", "customResolution", "customWidth", "customHeight"]));
            isInitialized.current = true;
        };

        loadObjects().then(() => {
            setTimeout(() => {
                if (fabricRef.current) {
                    fabricRef.current.renderAll();
                    console.log("Final render");
                }
            }, 500);
        });

        const handleSelection = (e) => {
            if (!e.selected || e.selected.length === 0) {
                setSelectedObject(null);
                return;
            }
            const selectedObj = e.selected[0];
            setSelectedObject(selectedObj);
            if (selectedObj.type === "i-text" || selectedObj.type === "textbox") {
                setFontSize(selectedObj.fontSize);
            }
            setForceUpdate(prev => prev + 1);
        };

        const handleSelectionCleared = () => {
            setSelectedObject(null);
        };

        fabricRef.current.on("selection:created", handleSelection);
        fabricRef.current.on("selection:updated", handleSelection);
        fabricRef.current.on("selection:cleared", handleSelectionCleared);

        fabricRef.current.on("mouse:down", (e) => {
            const target = e.target;
            console.log("Clicked object:", target ? {
                type: target.type,
                zIndex: target.zIndex,
                customResolution: target.customResolution,
                customWidth: target.customWidth,
                customHeight: target.customHeight,
            } : "No target");
            if (e.e.button === 2) {
                e.e.preventDefault();
                setContextMenu({ visible: true, x: e.e.clientX, y: e.e.clientY });
            } else {
                setContextMenu({ visible: false, x: 0, y: 0 });
            }
        });

        fabricRef.current.on("object:modified", () => {
            setCanvasObjects(fabricRef.current.toJSON(["zIndex", "customResolution", "customWidth", "customHeight"]));
        });

        return () => {
            if (fabricRef.current) {
                fabricRef.current.off("selection:created", handleSelection);
                fabricRef.current.off("selection:updated", handleSelection);
                fabricRef.current.off("selection:cleared", handleSelectionCleared);
                fabricRef.current.off("mouse:down");
                fabricRef.current.off("object:modified");
                fabricRef.current.dispose();
                fabricRef.current = null;
            }
            isInitialized.current = false;
        };
    }, [fontsLoaded, composedPoster]);

    useEffect(() => {
        if (!showImageSelector) return;

        const fetchImageOptions = async () => {
            setIsLoadingImages(true);
            try {
                const images = fabricRef.current.getObjects('image')
                    .filter(obj => !obj.isBackground && obj.selectable)
                    .map(obj => ({
                        url: obj.getSrc(),
                        object: obj,
                        resolution: obj.customResolution || null,
                        width: obj.width * obj.scaleX,
                        height: obj.height * obj.scaleY,
                    }));

                setImageOptions(images);
            } catch (error) {
                console.error('Error fetching image options:', error);
                setImageOptions([]);
            } finally {
                setIsLoadingImages(false);
            }
        };

        fetchImageOptions();
    }, [showImageSelector]);

    useEffect(() => {
        if (!fabricRef.current) return;
        fabricRef.current.setBackgroundColor(canvasBackgroundColor, fabricRef.current.renderAll.bind(fabricRef.current));
    }, [canvasBackgroundColor]);

    const downloadCanvas = (format) => {
        if (!fabricRef.current) return;

        let dataUrl;
        switch (format) {
            case "PNG":
                dataUrl = fabricRef.current.toDataURL({ format: "png", quality: 1 });
                break;
            case "JPG":
                dataUrl = fabricRef.current.toDataURL({ format: "jpeg", quality: 0.8 });
                break;
            case "SVG":
                dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(fabricRef.current.toSVG());
                break;
            default:
                return;
        }

        const link = document.createElement("a");
        link.download = `poster.${format.toLowerCase()}`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setShowDownloadMenu(false);
    };

    const addText = (x, y) => {
        if (!fabricRef.current) return;

        const text = new fabric.Textbox("Double click to edit", {
            left: x,
            top: y,
            width: 300,
            fontSize: fontSize,
            fill: "#333333",
            fontFamily: "Arial",
            originX: 'center',
            originY: 'center',
            zIndex: fabricRef.current.getObjects().length,
            selectable: true,
            evented: true,
        });

        patchTextObject(text);
        fabricRef.current.add(text);
        fabricRef.current.setActiveObject(text);
        setSelectedObject(text);
        setContextMenu({ visible: false, x: 0, y: 0 });
        fabricRef.current.renderAll();
        setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
    };

    const initiateImageUpload = (x, y) => {
        fileInputRef.current.setAttribute("data-x", x);
        fileInputRef.current.setAttribute("data-y", y);
        fileInputRef.current.click();
        setContextMenu({ visible: false, x: 0, y: 0 });
    };

    const handleImageUpload = (e) => {
        if (!fabricRef.current) return;

        const file = e.target.files[0];
        if (file) {
            const x = parseFloat(fileInputRef.current.getAttribute("data-x"));
            const y = parseFloat(fileInputRef.current.getAttribute("data-y"));

            const reader = new FileReader();
            reader.onload = (event) => {
                fabric.Image.fromURL(event.target.result, (img) => {
                    const targetWidth = 200;
                    const scale = targetWidth / img.width;
                    const targetHeight = img.height * scale;
                    img.set({
                        left: x,
                        top: y,
                        originX: 'center',
                        originY: 'center',
                        selectable: true,
                        evented: true,
                        customWidth: targetWidth,
                        customHeight: targetHeight,
                        zIndex: fabricRef.current.getObjects().length,
                    });
                    img.scaleToWidth(targetWidth);
                    fabricRef.current.add(img);
                    fabricRef.current.setActiveObject(img);
                    setSelectedObject(img);
                    fabricRef.current.renderAll();
                    setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
                }, { crossOrigin: "anonymous" });
            };
            reader.readAsDataURL(file);
            e.target.value = "";
        }
    };

    const addShape = (type) => {
        if (!fabricRef.current) return;

        let shape;
        const commonProps = {
            left: 100,
            top: 100,
            fill: "#FFFFFF",
            width: 100,
            height: 100,
            originX: 'center',
            originY: 'center',
            zIndex: fabricRef.current.getObjects().length,
            selectable: true,
            evented: true,
        };

        switch (type) {
            case "rectangle":
                shape = new fabric.Rect({ ...commonProps, rx: 0, ry: 0 });
                break;
            case "circle":
                shape = new fabric.Circle({ ...commonProps, radius: 50 });
                break;
            case "triangle":
                shape = new fabric.Triangle(commonProps);
                break;
            case "star": {
                const points = 5;
                const innerRadius = 20;
                const outerRadius = 40;
                const path = [];
                for (let i = 0; i < points * 2; i++) {
                    const radius = i % 2 === 0 ? outerRadius : innerRadius;
                    const angle = (i * Math.PI) / points;
                    path.push({ x: radius * Math.sin(angle), y: -radius * Math.cos(angle) });
                }
                shape = new fabric.Polygon(path, { ...commonProps, left: 150, top: 150 });
                break;
            }
            case "hexagon": {
                const hexPoints = [];
                for (let i = 0; i < 6; i++) {
                    const angle = (i * 2 * Math.PI) / 6;
                    hexPoints.push({ x: 50 * Math.cos(angle), y: 50 * Math.sin(angle) });
                }
                shape = new fabric.Polygon(hexPoints, { ...commonProps, left: 150, top: 150 });
                break;
            }
            default:
                break;
        }

        if (shape) {
            fabricRef.current.add(shape);
            fabricRef.current.setActiveObject(shape);
            setSelectedObject(shape);
            fabricRef.current.renderAll();
            setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
        }
        setShowElementMenu(false);
    };

    const updateObjectProperty = (property, value) => {
        if (!selectedObject || !fabricRef.current) return;
        if (property === "fontWeight" && value === "bold" && !currentFontSupportsBold()) {
            console.warn(`Bold not applied to ${selectedObject.fontFamily}`);
            return;
        }

        selectedObject.set(property, value);

        if (property === "fontFamily" || property === "fontWeight" || property === "fontStyle") {
            patchTextObject(selectedObject);
        }

        if (property === "fontSize") {
            setFontSize(value);
        }

        setForceUpdate(prev => prev + 1);
        fabricRef.current.requestRenderAll();
        setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
    };

    const deleteSelected = () => {
        if (!selectedObject || !fabricRef.current) return;

        fabricRef.current.remove(selectedObject);
        setSelectedObject(null);
        fabricRef.current.renderAll();
        setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
    };

    const adjustFontSize = (increase) => {
        const isText = selectedObject?.type === "i-text" || selectedObject?.type === "textbox";
        if (!selectedObject || !isText) return;
        const newSize = increase ? fontSize + 2 : fontSize - 2;
        setFontSize(newSize);
        updateObjectProperty("fontSize", newSize);
    };

    const moveObjectLayer = (direction) => {
        if (!selectedObject || !fabricRef.current) return;

        if (direction === 'up') {
            fabricRef.current.bringForward(selectedObject);
        } else if (direction === 'down') {
            fabricRef.current.sendBackwards(selectedObject);
        } else if (direction === 'top') {
            fabricRef.current.bringToFront(selectedObject);
        } else if (direction === 'bottom') {
            fabricRef.current.sendToBack(selectedObject);
        }

        fabricRef.current.getObjects().forEach((obj, index) => {
            obj.zIndex = index;
        });

        fabricRef.current.renderAll();
        setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
    };

    const handleImageSelectForGeneration = (image) => {
        setSelectedImageForGeneration({
            object: image.object,
            resolution: image.resolution,
            templateId: templateId,
            width: image.width,
            height: image.height,
        });
        setShowImageSelector(false);
        setShowImageGenerator(true);
    };

    const handleGenerateImage = async () => {
        if (!selectedImageForGeneration || !fabricRef.current) return;

        setIsGeneratingImage(true);
        console.log("SELECTED IMAGE FOR GENERATION:", selectedImageForGeneration);

        try {
            console.log(
                "Generating image with prompt:",
                imagePrompt,
                "templateId:",
                selectedImageForGeneration.templateId,
                "width:",
                selectedImageForGeneration.width,
                "height:",
                selectedImageForGeneration.height,
                "zIndex:",
                selectedImageForGeneration.object.zIndex,
                "provider:",
                selectedGenAIModel
            );

            const requestBody = {
                image_parameters: {
                    query: imagePrompt,
                    width: selectedImageForGeneration.width,
                    height: selectedImageForGeneration.height,
                },
                template_id: selectedImageForGeneration.templateId,
                provider_name: selectedGenAIModel,
            };
            if (selectedImageForGeneration.resolution) {
                requestBody.image_parameters.resolution = selectedImageForGeneration.resolution;
            }

            const response = await fetch('/api/v2/posters/regenerate-image', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            console.log("Backend response:", response);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`HTTP error! status: ${response.status}, detail: ${errorData.detail || 'Unknown error'}`);
            }

            const data = await response.json();
            const newImage = data['newImage'];
            const newImageUrl = `data:image/png;base64,${newImage}`;

            if (!newImage || typeof newImage !== 'string' || newImage.length === 0) {
                throw new Error("Invalid base64 string received from backend");
            }

            const prevImage = selectedImageForGeneration.object;
            const index = fabricRef.current.getObjects().indexOf(prevImage);

            fabricRef.current.remove(prevImage);

            fabric.Image.fromURL(
                newImageUrl,
                (img) => {
                    if (!img) {
                        console.error("Failed to create image object from data URL");
                        alert("Failed to load the generated image. Please try again.");
                        return;
                    }

                    const newImageProps = {
                        left: prevImage.left || 0,
                        top: prevImage.top || 0,
                        angle: prevImage.angle || 0,
                        scaleX: prevImage.scaleX || 1,
                        scaleY: prevImage.scaleY || 1,
                        flipX: prevImage.flipX || false,
                        flipY: prevImage.flipY || false,
                        opacity: prevImage.opacity || 1,
                        selectable: true,
                        evented: true,
                        zIndex: index,
                        customResolution: selectedImageForGeneration.resolution || prevImage.customResolution || null,
                        customWidth: selectedImageForGeneration.width || prevImage.customWidth || null,
                        customHeight: selectedImageForGeneration.height || prevImage.customHeight || null,
                        clipPath: prevImage.clipPath ? { ...prevImage.clipPath } : null,
                    };

                    img.set(newImageProps);
                    img.scaleToWidth(newImageProps.customWidth);

                    fabricRef.current.insertAt(img, index, false);

                    fabricRef.current.getObjects().forEach((obj, idx) => {
                        obj.zIndex = idx;
                    });

                    fabricRef.current.setActiveObject(img);
                    setSelectedObject(img);

                    fabricRef.current.renderAll();
                    setCanvasObjects(
                        fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight'])
                    );

                    console.log("New image added with properties:", {
                        zIndex: img.zIndex,
                        customResolution: img.customResolution,
                        customWidth: img.customWidth,
                        customHeight: img.customHeight,
                        left: img.left,
                        top: img.top,
                        clipPath: !!img.clipPath,
                        angle: img.angle,
                        scaleX: img.scaleX,
                        scaleY: img.scaleY,
                    });

                    logCanvasObjects();
                },
                { crossOrigin: "anonymous" }
            );

            setShowImageGenerator(false);
            setSelectedImageForGeneration(null);
            setImagePrompt("");
        } catch (error) {
            console.error('Error generating image:', error);
            alert(`Failed to generate image: ${error.message}`);
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleSearchImages = async () => {
        if (!fabricRef.current || !searchQuery) return;

        setIsSearchingImages(true);
        try {
            const response = await fetch('/api/v2/posters/search-images', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: searchQuery,
                    orientation: 'portrait',
                    per_page: 20
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`HTTP error! status: ${response.status}, detail: ${errorData.detail || 'Unknown error'}`);
            }

            const data = await response.json();
            setPexelsImages(data.images || []);
        } catch (error) {
            console.error('Error searching images:', error);
            alert(`Failed to search images: ${error.message}`);
        } finally {
            setIsSearchingImages(false);
        }
    };

    const handleSelectPexelsImage = (image) => {
        if (!fabricRef.current) return;

        fabric.Image.fromURL(image.url, (img) => {
            const targetWidth = 200;
            const scale = targetWidth / img.width;
            const targetHeight = img.height * scale;
            img.set({
                left: fabricRef.current.width / 2,
                top: fabricRef.current.height / 2,
                originX: 'center',
                originY: 'center',
                selectable: true,
                evented: true,
                customWidth: targetWidth,
                customHeight: targetHeight,
                zIndex: fabricRef.current.getObjects().length,
            });
            img.scaleToWidth(targetWidth);
            fabricRef.current.add(img);
            fabricRef.current.setActiveObject(img);
            setSelectedObject(img);
            fabricRef.current.renderAll();
            setCanvasObjects(fabricRef.current.toJSON(['zIndex', 'customResolution', 'customWidth', 'customHeight']));
        }, { crossOrigin: "anonymous" });

        setShowSearchImages(false);
        setSearchQuery("");
        setPexelsImages([]);
    };

    const handleGeneratePoster = async (e) => {
        e.preventDefault();
        if (!templateId || !posterDescription) {
            alert("Please select a template and provide a poster description.");
            return;
        }

        setIsGeneratingPoster(true);
        try {
            const requestBody = {
                template_id: templateId,
                poster_description: posterDescription,
                additional_options: {},
                provider_name: posterGenAIModel,
            };

            const response = await fetch('/api/v2/posters/generate-poster', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`HTTP error! status: ${response.status}, detail: ${errorData.detail || 'Unknown error'}`);
            }

            const data = await response.json();
            console.log("Generated poster:", data);
            alert("Poster generated successfully! Please check the canvas.");
            setPosterDescription("");
        } catch (error) {
            console.error('Error generating poster:', error);
            alert(`Failed to generate poster: ${error.message}`);
        } finally {
            setIsGeneratingPoster(false);
        }
    };

    const isText = selectedObject?.type === "i-text" || selectedObject?.type === "textbox";
    const isFontWithoutBold = isText && !currentFontSupportsBold();

    if (!fontsLoaded) {
        return (
            <div className="loading-fonts">
                <div>Loading fonts...</div>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div
            className="editor-wrapper"
            style={{ pointerEvents: isAnyPopupOpen ? 'none' : 'auto' }}
        >
            <div
                className={`toolbar sticky ${isAnyPopupOpen ? 'popup-open' : ''}`}
                style={{ pointerEvents: isAnyPopupOpen ? 'none' : 'auto' }}
            >
                <div className="tools-group">
                    <div className="tool-item">
                        <button
                            onClick={() => addText(100, 100)}
                            className="tool-button"
                            title="Add Text"
                        >
                            <Type size={20} />
                        </button>
                        <span className="tool-label">Text</span>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={() => initiateImageUpload(100, 100)}
                            className="tool-button"
                            title="Upload Image"
                        >
                            <ImageIcon size={20} />
                        </button>
                        <span className="tool-label">Image</span>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                    />

                    <div className="tool-item">
                        <button
                            onClick={() => setShowImageSelector(true)}
                            className="tool-button"
                            title="Generate Image"
                        >
                            <Wand size={20} />
                        </button>
                        <span className="tool-label">Generate</span>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={() => setShowSearchImages(true)}
                            className="tool-button"
                            title="Search Images"
                        >
                            <Search size={20} />
                        </button>
                        <span className="tool-label">Search</span>
                    </div>

                    <div className="tool-item" ref={shapesMenuRef}>
                        <div className="relative">
                            <button
                                onClick={() => setShowElementMenu(!showElementMenu)}
                                className="tool-button"
                                title="Add Shape"
                            >
                                <Square size={20} />
                            </button>
                            <span className="tool-label">Shapes</span>

                            {showElementMenu && (
                                <div className="shapes-dropdown">
                                    <button onClick={() => addShape("rectangle")} className="dropdown-item">
                                        <Square size={16} />
                                        <span>Rectangle</span>
                                    </button>
                                    <button onClick={() => addShape("circle")} className="dropdown-item">
                                        <CircleIcon size={16} />
                                        <span>Circle</span>
                                    </button>
                                    <button onClick={() => addShape("triangle")} className="dropdown-item">
                                        <Triangle size={16} />
                                        <span>Triangle</span>
                                    </button>
                                    <button onClick={() => addShape("star")} className="dropdown-item">
                                        <Star size={16} />
                                        <span>Star</span>
                                    </button>
                                    <button onClick={() => addShape("hexagon")} className="dropdown-item">
                                        <Hexagon size={16} />
                                        <span>Hexagon</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tool-item" ref={layerMenuRef}>
                        <div className="relative">
                            <button
                                onClick={() => selectedObject && setShowLayerMenu(!showLayerMenu)}
                                className={`tool-button ${!selectedObject ? 'disabled' : ''}`}
                                title="Arrange Layers"
                                disabled={!selectedObject}
                            >
                                <Layers size={20} />
                            </button>
                            <span className="tool-label">Layers</span>

                            {showLayerMenu && selectedObject && (
                                <div className="layers-dropdown">
                                    <button onClick={() => moveObjectLayer('top')} className="dropdown-item">
                                        <ArrowUp size={16} />
                                        <span>Bring to Front</span>
                                    </button>
                                    <button onClick={() => moveObjectLayer('up')} className="dropdown-item">
                                        <ArrowUp size={16} className="opacity-50" />
                                        <span>Bring Forward</span>
                                    </button>
                                    <button onClick={() => moveObjectLayer('down')} className="dropdown-item">
                                        <ArrowDown size={16} className="opacity-50" />
                                        <span>Send Backward</span>
                                    </button>
                                    <button onClick={() => moveObjectLayer('bottom')} className="dropdown-item">
                                        <ArrowDown size={16} />
                                        <span>Send to Back</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tool-item" ref={downloadMenuRef}>
                        <div className="relative">
                            <button
                                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                className="tool-button"
                                title="Download"
                            >
                                <Download size={20} />
                            </button>
                            <span className="tool-label">Download</span>

                            {showDownloadMenu && (
                                <div className="download-dropdown">
                                    <button onClick={() => downloadCanvas("PNG")} className="dropdown-item">
                                        <span>PNG</span>
                                    </button>
                                    <button onClick={() => downloadCanvas("JPG")} className="dropdown-item">
                                        <span>JPG</span>
                                    </button>
                                    <button onClick={() => downloadCanvas("SVG")} className="dropdown-item">
                                        <span>SVG</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={deleteSelected}
                            className={`tool-button text-red-500 ${!selectedObject ? 'disabled' : ''}`}
                            title="Delete"
                            disabled={!selectedObject}
                        >
                            <Trash2 size={20} />
                        </button>
                        <span className="tool-label">Delete</span>
                    </div>
                </div>

                <div className="tools-group">
                    <div className="tool-item">
                        <input
                            type="color"
                            value={selectedObject?.fill || "#333333"}
                            onChange={(e) => updateObjectProperty("fill", e.target.value)}
                            className="tool-color"
                            title="Change Color"
                            disabled={!isText}
                        />
                        <span className="tool-label">Color</span>
                    </div>

                    <select
                        value={selectedObject?.fontFamily || "Arial"}
                        onChange={(e) => updateObjectProperty("fontFamily", e.target.value)}
                        className="tool-select"
                        style={{ fontFamily: selectedObject?.fontFamily || "Arial" }}
                        disabled={!isText}
                    >
                        {FONTS.map((font) => (
                            <option key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                            </option>
                        ))}
                    </select>

                    <div className="font-size-control">
                        <button
                            onClick={() => adjustFontSize(false)}
                            className="tool-button"
                            disabled={!isText}
                        >
                            <Minus size={16} />
                        </button>
                        <span className="font-size-display">{fontSize}</span>
                        <button
                            onClick={() => adjustFontSize(true)}
                            className="tool-button"
                            disabled={!isText}
                        >
                            <Plus size={16} />
                        </button>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={() =>
                                updateObjectProperty(
                                    "fontWeight",
                                    selectedObject?.fontWeight === "bold" ? "normal" : "bold"
                                )
                            }
                            className={`tool-button ${selectedObject?.fontWeight === "bold" ? "active" : ""} ${
                                isFontWithoutBold ? "disabled" : ""
                            }`}
                            disabled={!isText || isFontWithoutBold}
                            title={isFontWithoutBold ? `${selectedObject?.fontFamily} doesn't support bold` : "Bold"}
                        >
                            <Bold size={20} />
                        </button>
                        <span className="tool-label">Bold</span>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={() =>
                                updateObjectProperty(
                                    "fontStyle",
                                    selectedObject?.fontStyle === "italic" ? "normal" : "italic"
                                )
                            }
                            className={`tool-button ${selectedObject?.fontStyle === "italic" ? "active" : ""}`}
                            disabled={!isText}
                        >
                            <Italic size={20} />
                        </button>
                        <span className="tool-label">Italic</span>
                    </div>

                    <div className="tool-item">
                        <button
                            onClick={() => updateObjectProperty("underline", !selectedObject?.underline)}
                            className={`tool-button ${selectedObject?.underline ? "active" : ""}`}
                            disabled={!isText}
                        >
                            <Underline size={20} />
                        </button>
                        <span className="tool-label">Underline</span>
                    </div>
                </div>
            </div>

            <div className="outer-canvas-container">
                <canvas id="canvas" ref={canvasRef} />
                {contextMenu.visible && (
                    <div
                        ref={contextMenuRef}
                        className="context-menu"
                        style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px`, position: "absolute" }}
                    >
                        <button onClick={() => addText(contextMenu.x, contextMenu.y)} className="context-menu-item">
                            <Type size={16} />
                            <span>Add Text</span>
                        </button>
                        <button
                            onClick={() => initiateImageUpload(contextMenu.x, contextMenu.y)}
                            className="context-menu-item"
                        >
                            <ImageIcon size={16} />
                            <span>Add Image</span>
                        </button>
                        <button
                            onClick={() => {
                                setShowSearchImages(true);
                                setContextMenu({ visible: false, x: 0, y: 0 });
                            }}
                            className="context-menu-item"
                        >
                            <Search size={16} />
                            <span>Search Images</span>
                        </button>
                    </div>
                )}

                {showImageSelector && (
                    <div className="popup-overlay">
                        <div className="popup-content">
                            <div className="popup-header">
                                <h3>Select an Image</h3>
                            </div>
                            <div className="popup-body">
                                {isLoadingImages ? (
                                    <div className="loading">Loading images...</div>
                                ) : imageOptions.length > 0 ? (
                                    <div className="image-grid">
                                        {imageOptions.map((image) => (
                                            <div key={image.url} className="image-option-wrapper">
                                                <img
                                                    src={image.url}
                                                    alt="Option"
                                                    className="image-option"
                                                    onClick={() => handleImageSelectForGeneration(image)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-images">No images available.</div>
                                )}
                            </div>
                            <div className="popup-footer">
                                <button
                                    onClick={() => setShowImageSelector(false)}
                                    className="popup-button popup-button-secondary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showImageGenerator && (
                    <div className="popup-overlay">
                        <div className="popup-content">
                            <div className="popup-header">
                                <h3>Generate Image</h3>
                            </div>
                            <div className="popup-body">
                                <textarea
                                    value={imagePrompt}
                                    onChange={(e) => setImagePrompt(e.target.value)}
                                    placeholder="Enter image generation prompt"
                                    className="prompt-textarea"
                                />
                                <div className="genai-model-selector">
                                    <label htmlFor="genai-model">Select GenAI Model:</label>
                                    <select
                                        id="genai-model"
                                        value={selectedGenAIModel}
                                        onChange={(e) => setSelectedGenAIModel(e.target.value)}
                                        className="tool-select"
                                    >
                                        <option value="ideogram">Ideogram</option>
                                        <option value="stable_diffusion_xl">Stable Diffusion XL</option>
                                        <option value="flux_schnell">FLUX.1</option>
                                    </select>
                                </div>
                            </div>
                            <div className="popup-footer">
                                <button
                                    onClick={handleGenerateImage}
                                    className="popup-button popup-button-primary"
                                    disabled={isGeneratingImage}
                                >
                                    {isGeneratingImage ? "Generating..." : "Generate"}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowImageGenerator(false);
                                        setSelectedImageForGeneration(null);
                                        setImagePrompt("");
                                    }}
                                    className="popup-button popup-button-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showSearchImages && (
                    <div className="popup-overlay">
                        <div className="popup-content">
                            <div className="popup-header">
                                <h3>Search Images</h3>
                            </div>
                            <div className="popup-body">
                                <div className="search-input-container">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Enter search query"
                                        className="search-input"
                                    />
                                    <button
                                        onClick={handleSearchImages}
                                        className="popup-button popup-button-primary"
                                        disabled={isSearchingImages || !searchQuery}
                                    >
                                        {isSearchingImages ? "Searching..." : "Search"}
                                    </button>
                                </div>
                                {pexelsImages.length > 0 ? (
                                    <div className="image-grid">
                                        {pexelsImages.map((image) => (
                                            <div key={image.id} className="image-option-wrapper">
                                                <img
                                                    src={image.url}
                                                    alt={image.alt || "Pexels Image"}
                                                    className="image-option"
                                                    onClick={() => handleSelectPexelsImage(image)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : isSearchingImages ? (
                                    <div className="loading">Searching images...</div>
                                ) : (
                                    <div className="no-images">No images found. Try a different search query.</div>
                                )}
                            </div>
                            <div className="popup-footer">
                                <button
                                    onClick={() => {
                                        setShowSearchImages(false);
                                        setSearchQuery("");
                                        setPexelsImages([]);
                                    }}
                                    className="popup-button popup-button-secondary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditorCanvas;